import {
    CreatorHeaderHeightPxDesktop,
    CreatorHeaderHeightPxMobile,
} from 'constants/creator';
import React from 'react';
import { Container, Box, Flex } from '@landr/maestro';
import { cva } from '@landr/styled-system/css';
import { useMatch } from 'react-router-dom';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { FooterNavigation, SocialNavigation } from 'components/Navigations';
import { RoutePaths } from 'types/enums';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import { useAppSettings } from 'contexts/AppSettingsContext/AppSettingsContext';
import { useAuthentication } from '../../contexts/AuthenticationProvider/AuthenticationProvider';
import Logo from './Logo';
import { LanguageDropdown } from './LanguageDropdown';

const footerRowStyles = cva({
    base: {
        display: 'flex',
        justifyContent: 'center',
        gap: '2xl',
        md: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 'none',
            alignItems: 'center',
            width: '100%',
            mb: 'xl',
        },
    },
    variants: {
        isFirstRow: {
            true: {
                base: {
                    flexDirection: 'column',
                    mb: '2xl',
                },
            },
            false: {
                base: {
                    flexDirection: 'column-reverse',
                    mb: 'none',
                },
            },
        },
    },
});

const footerStyles = cva({
    base: {
        flexShrink: 0,
        fontWeight: 'bold',
        paddingY: '10px',
        paddingX: 'none',
        '& > div:first-child': {
            marginBottom: 0,
        },
    },
    variants: {
        isCreatorVisible: {
            true: {
                '& > div:first-child': {
                    marginBottom: `${CreatorHeaderHeightPxDesktop}px`,
                    lg: {
                        marginBottom: `${CreatorHeaderHeightPxMobile}px`,
                    },
                },
            },
        },
    },
});

export const useFooter = () => {
    const { isFooterAllowed } = useAppSettings();
    const { isAuthenticated } = useAuthentication();

    const { isVisible: isCreatorVisible } = useCreatorContext();
    const { isDAWPlugin } = useExecutionEnvironmentContext();

    /**
     * Don't render the Footer when is a guest user on private routes.
     * This prevents flickering of the UI in between redirects to authentication services.
     */

    const isDownloadPath = useMatch(`/${RoutePaths.Downloads}`);
    const isFavoritesPath = useMatch(`/${RoutePaths.Favorites}`);
    const isCollectionsPath = useMatch(`/${RoutePaths.UserCollections}`);

    const isNotPrivateRoute =
        !isDownloadPath && !isFavoritesPath && !isCollectionsPath;

    const isFooterVisible =
        isFooterAllowed &&
        (isAuthenticated || (!isDAWPlugin && isNotPrivateRoute));

    return {
        isFooterVisible,
        isCreatorVisible,
        isAuthenticated,
    };
};

const Footer: React.FC = () => {
    const { isFooterVisible, isCreatorVisible, isAuthenticated } = useFooter();

    if (!isFooterVisible) {
        return null;
    }

    return (
        <Box
            as="footer"
            data-testid="Footer"
            className={footerStyles({ isCreatorVisible })}
            mt="3xl"
        >
            <Container px="md">
                <Flex className={footerRowStyles({ isFirstRow: true })}>
                    <Box>
                        <SocialNavigation />
                    </Box>
                    {!isAuthenticated && (
                        <Box alignSelf="center">
                            <LanguageDropdown />
                        </Box>
                    )}
                </Flex>
                <Flex className={footerRowStyles({ isFirstRow: false })}>
                    <Box>
                        <Logo />
                    </Box>
                    <Box>
                        <FooterNavigation />
                    </Box>
                </Flex>
            </Container>
        </Box>
    );
};

export default Footer;
